import { useQueryClient } from "@tanstack/react-query"

import { useExtendWithSubnetworks, useNetworksStateContext } from "@l2r-front/l2r-networks"

import { API_MODULE_HORIZONTAL_SIGNING } from "../../../../../common/constants/modules"

import { horizontalSigningQueryKeys } from "./queryKeys"
import { useHorizontalSigningQueries, useHorizontalSigningQuery } from "./useHorizontalSigningQueries"

export function useHorizontalSigns(parameters = {}, config = {}) {
    const url = "horizontal_signs/"
    const { selectedNetwork } = useNetworksStateContext()

    const {
        filterBySubnetworks = true,
        ...otherConfig
    } = config

    const queryParameters = {
        ...parameters,
        project: selectedNetwork.slug,
    }

    const extendedQueryParameters = useExtendWithSubnetworks(queryParameters, API_MODULE_HORIZONTAL_SIGNING)
    const parametersToUse = filterBySubnetworks ? extendedQueryParameters : queryParameters

    const queryKey = horizontalSigningQueryKeys["layer"](parametersToUse)

    return useHorizontalSigningQuery(
        url,
        queryKey,
        parametersToUse,
        {
            enabled: !!selectedNetwork,
            ...otherConfig,
        },
    )
}

export const useExistingHorizontalSign = (signId, onError) => {

    const queryClient = useQueryClient()
    const signQueryKey = horizontalSigningQueryKeys["feature"](signId)
    signQueryKey.pop()
    const layerQueryKey = horizontalSigningQueryKeys["layer"]()
    layerQueryKey.pop()

    const signsDataFromLayer = queryClient.getQueriesData({ queryKey: layerQueryKey })
    const signsData = queryClient.getQueriesData({ queryKey: signQueryKey })

    const existingInSignData = signsData?.some(([_, signData]) => {
        return (signData?.id || signData?.uuid) === signId
    })
    const existingInLayerData = signsDataFromLayer?.some(([_, signsDataFromLayer]) => {
        if (signsDataFromLayer?.features) {
            return signsDataFromLayer?.features?.some(sign => {
                return sign.id === signId
            })
        } else {
            return signsDataFromLayer?.some(sign => sign.uuid === signId)
        }
    })
    const existingSignInCache = existingInSignData || existingInLayerData

    const { isLoading, isError } = useHorizontalSign(signId, {}, {
        enabled: !existingSignInCache,
        onError,
    })

    return (!(isLoading || isError) || existingSignInCache)
}

export function useHorizontalSign(signId, parameters = {}, config = {}) {
    const url = `horizontal_signs/${signId}/`
    const { selectedNetwork } = useNetworksStateContext()

    const queryParameters = {
        ...parameters,
        project: selectedNetwork.slug,
    }

    const queryKey = horizontalSigningQueryKeys["feature"](signId, parameters)

    return useHorizontalSigningQuery(
        url,
        queryKey,
        queryParameters,
        {
            enabled: !!selectedNetwork,
            autoRefetch: true,
            ...config,
        },
    )
}

export function useHorizontalSignsQueries(queries = [], globalConfig = {}) {
    const url = "horizontal_signs/"
    const extendedQueryParameters = useExtendWithSubnetworks(
        queries.map(query => query.queryParameters),
        API_MODULE_HORIZONTAL_SIGNING,
    )
    const { filterBySubnetworks = true } = globalConfig

    const queriesData = queries.map((query, index) => {
        const queryParameters = filterBySubnetworks ? extendedQueryParameters[index] : queries[index].queryParameters
        const queryKey = horizontalSigningQueryKeys["layer"](queryParameters)

        return {
            url,
            queryKey,
            queryParameters,
            config: query.config,
        }
    })

    return useHorizontalSigningQueries(queriesData)
}